// WebAuthn helpers: convert browser PublicKeyCredential objects into the exact
// JSON shape the backend (webauthn-lib PublicKeyCredentialLoader) expects.
//
// Encoding requirements (must match the loader exactly):
//   id                 -> base64url, NO padding
//   rawId              -> standard base64 (+ /)
//   response.clientDataJSON -> base64url, NO padding
//   response.attestationObject (registration) -> standard base64
//   response.authenticatorData (assertion)    -> base64url, NO padding
//   response.signature (assertion)            -> standard base64
//   response.userHandle (assertion)           -> standard base64 (or null)

function bufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

function bufferToBase64UrlNoPad(buffer: ArrayBuffer): string {
    return bufferToBase64(buffer)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

function base64UrlToBuffer(str: string): ArrayBuffer {
    const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

// The backend returns base64url strings for the binary fields; the browser
// WebAuthn API expects ArrayBuffer/BufferSource. Convert before calling it.
export function prepareCreationOptions(options: any): PublicKeyCredentialCreationOptions {
    return {
        rp: options.rp,
        user: {
            ...options.user,
            id: base64UrlToBuffer(options.user.id),
        },
        challenge: base64UrlToBuffer(options.challenge),
        pubKeyCredParams: options.pubKeyCredParams,
        authenticatorSelection: options.authenticatorSelection,
        attestation: options.attestation,
        timeout: options.timeout,
    };
}

export function prepareRequestOptions(options: any): PublicKeyCredentialRequestOptions {
    return {
        challenge: base64UrlToBuffer(options.challenge),
        rpId: options.rpId,
        allowCredentials: options.allowCredentials?.map((c: any) => ({
            type: c.type,
            id: base64UrlToBuffer(c.id),
            transports: c.transports,
        })),
        userVerification: options.userVerification,
        timeout: options.timeout,
    };
}

export function serializeCredential(credential: PublicKeyCredential): object {
    const response: Record<string, unknown> = {
        clientDataJSON: bufferToBase64UrlNoPad(credential.response.clientDataJSON),
    };

    const attestation = credential.response as AuthenticatorAttestationResponse;
    const assertion = credential.response as AuthenticatorAssertionResponse;

    if (attestation.attestationObject) {
        // registration
        response.attestationObject = bufferToBase64(attestation.attestationObject);
    }
    if (assertion.signature) {
        // assertion
        response.authenticatorData = bufferToBase64UrlNoPad(assertion.authenticatorData);
        response.signature = bufferToBase64(assertion.signature);
        response.userHandle = assertion.userHandle ? bufferToBase64(assertion.userHandle) : null;
    }

    return {
        id: credential.id,
        rawId: bufferToBase64(credential.rawId),
        type: credential.type,
        response,
    };
}
